import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    borderRadius: '14px',
    backgroundColor: theme.palette.common.white,
    border: '1px solid rgba(15, 34, 52, 0.06)',
    boxShadow: '0 8px 24px rgba(15, 34, 52, 0.06)',
    cursor: 'pointer',
    transition: 'transform 180ms ease, box-shadow 180ms ease',
    '@media (hover: hover)': {
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 32px rgba(15, 34, 52, 0.1)',
      },
    },
    // photoStage sits above the overlay, so the ring lives on the card root.
    '&:focus-within': {
      outline: `2px solid ${theme.palette.secondary.main}`,
      outlineOffset: 2,
    },
  },
  /** Group-event host / other non-grid embeds: match sidebar book card radius; clip photo to corners. */
  rootEmbeddedOverride: {
    flexDirection: 'row',
    alignItems: 'stretch',
    minHeight: 168,
    height: 'auto',
    overflow: 'visible',
    borderRadius: 12,
    border: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: '0 8px 28px rgba(0, 10, 22, 0.08)',
  },
  /** No profile to open (e.g. PGA HOPE academy staff): drop the pointer and hover lift. */
  rootStatic: {
    cursor: 'default',
    '@media (hover: hover)': {
      '&:hover': {
        transform: 'none',
        boxShadow: '0 8px 24px rgba(15, 34, 52, 0.06)',
      },
    },
  },
  cardOverlayLink: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    display: 'block',
    cursor: 'pointer',
    borderRadius: 'inherit',
    // Transparent fill so empty overlay anchors still receive clicks.
    backgroundColor: 'rgba(0, 0, 0, 0)',
    '&:focus, &:focus-visible': {
      outline: 'none',
    },
  },
  photoStage: {
    position: 'relative',
    zIndex: 3,
    width: '100%',
    aspectRatio: '1 / 1',
    overflow: 'hidden',
    background: `linear-gradient(180deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.dark} 100%)`,
    pointerEvents: 'none',
  },
  /** Sidebar / group-event host: photo on the left, content on the right. */
  photoStageEmbeddedOverride: {
    flex: '0 0 40%',
    width: '40%',
    aspectRatio: 'auto',
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
  },
  coachImage: {
    width: '100%',
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    objectFit: 'cover',
    border: 'none',
    boxShadow: 'none',
    background: `linear-gradient(180deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.dark} 100%)`,
  },
  coachInitials: {
    fontFamily: '"Source Serif Pro", serif',
    fontSize: '40px',
    fontWeight: 600,
    color: theme.palette.common.white,
    lineHeight: 1,
    textTransform: 'uppercase',
  },
  photoScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    height: '62%',
    background: 'linear-gradient(180deg, rgba(0, 17, 35, 0) 0%, rgba(0, 17, 35, 0.82) 100%)',
    pointerEvents: 'none',
  },
  photoCaption: {
    position: 'absolute',
    left: 12,
    right: 12,
    bottom: 12,
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    pointerEvents: 'none',
  },
  // Clear the 36px play control (right: 10) plus a small gap. The name sits
  // above "Watch Intro", so it does not need that label's full width.
  photoCaptionWithVideo: {
    right: 54,
  },
  saveDock: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 3,
    pointerEvents: 'auto',
  },
  saveToggle: {
    padding: theme.spacing(0.75),
    cursor: 'pointer',
    color: theme.palette.common.white,
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.55))',
    '& .MuiSvgIcon-root': {
      fontSize: 22,
    },
    '&:hover': {
      color: theme.palette.common.white,
    },
    '&:focus-visible': {
      outlineOffset: 2,
    },
  },
  saveToggleActive: {
    color: theme.palette.common.white,
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
  videoControl: {
    position: 'absolute',
    right: 10,
    bottom: 10,
    zIndex: 4,
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    appearance: 'none',
    pointerEvents: 'auto',
    flexShrink: 0,
    '&:hover $videoBadge, &:focus-visible $videoBadge, &:active $videoBadge': {
      backgroundColor: theme.palette.secondary.light,
      transform: 'scale(1.06)',
    },
    '&:hover $watchIntroLabel, &:focus-visible $watchIntroLabel, &:active $watchIntroLabel': {
      opacity: 1,
      color: theme.palette.common.white,
    },
    '&:focus-visible': {
      outline: 'none',
    },
    '&:focus-visible $videoBadge': {
      outline: `2px solid ${theme.palette.common.white}`,
      outlineOffset: 2,
      boxShadow: `0 0 0 4px ${theme.palette.secondary.main}`,
    },
  },
  videoBadge: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: theme.palette.secondary.main,
    border: `2px solid ${theme.palette.common.white}`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
    boxShadow: '0 2px 10px rgba(0, 35, 75, 0.32)',
    transition: 'transform 140ms ease, background-color 140ms ease',
  },
  videoBadgeIcon: {
    fontSize: 22,
    marginLeft: 2,
  },
  watchIntroLabel: {
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.01em',
    color: theme.palette.common.white,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    textShadow: '0 1px 4px rgba(0, 0, 0, 0.65)',
    opacity: 0,
    transition: 'opacity 140ms ease',
  },
  contentContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    flex: 1,
    padding: '12px 14px 14px',
    textAlign: 'left',
    pointerEvents: 'none',
  },
  contentContainerEmbeddedOverride: {
    flex: '1 1 0',
    minWidth: 0,
    justifyContent: 'center',
    alignItems: 'flex-start',
    textAlign: 'left',
    '& $contentHeader': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      gap: 8,
      marginBottom: 8,
    },
    '& $statsBlock': {
      marginRight: 0,
    },
  },
  coachName: {
    fontFamily: '"Source Serif Pro", serif',
    color: theme.palette.common.white,
    fontSize: '22px',
    lineHeight: 1.15,
    fontWeight: 700,
    margin: 0,
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'break-word',
    textShadow: '0 1px 8px rgba(0, 0, 0, 0.55)',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px',
    },
  },
  photoFacility: {
    margin: 0,
    maxWidth: '100%',
    fontSize: '13px',
    fontWeight: 700,
    color: theme.palette.common.white,
    lineHeight: 1.3,
    wordBreak: 'break-word',
    textShadow: '0 1px 6px rgba(0, 0, 0, 0.6)',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    '&.MuiTypography-root': {
      fontWeight: 700,
    },
  },
  photoPlace: {
    margin: 0,
    maxWidth: '100%',
    fontSize: '13px',
    fontWeight: 400,
    color: theme.palette.common.white,
    lineHeight: 1.3,
    wordBreak: 'break-word',
    textShadow: '0 1px 6px rgba(0, 0, 0, 0.6)',
    '&.MuiTypography-root': {
      fontWeight: 400,
    },
  },
  /** Embedded layout: name / facility / place stacked above the stats row on a white background. */
  embeddedIdentity: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    minWidth: 0,
    marginBottom: 8,
  },
  coachNameEmbedded: {
    color: theme.palette.secondary.main,
    fontSize: '18px',
    lineHeight: 1.2,
    textShadow: 'none',
    [theme.breakpoints.down('sm')]: {
      fontSize: '18px',
    },
  },
  photoFacilityEmbedded: {
    color: theme.palette.grey.darkest,
    fontSize: '13px',
    lineHeight: 1.3,
    textShadow: 'none',
    '&.MuiTypography-root': {
      fontWeight: 600,
    },
  },
  photoPlaceEmbedded: {
    color: theme.palette.grey.dark,
    fontSize: '12px',
    lineHeight: 1.3,
    textShadow: 'none',
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: 12,
    marginBottom: 10,
  },
  statsBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    minWidth: 0,
    marginRight: 'auto',
  },
  bioText: {
    margin: '0 0 4px',
    minWidth: 0,
    overflowWrap: 'break-word',
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    '&.MuiTypography-root': {
      fontSize: '14px',
      fontWeight: 500,
      color: theme.palette.grey.darkest,
      lineHeight: 1.4,
    },
  },
  priceText: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 700,
    color: theme.palette.secondary.main,
    lineHeight: 1.4,
  },
  distanceText: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    margin: 0,
    fontSize: '13px',
    fontWeight: 400,
    fontStyle: 'normal',
    color: theme.palette.grey.dark,
    lineHeight: 1.4,
  },
  distanceIcon: {
    fontSize: 16,
    width: 16,
    height: 16,
    flexShrink: 0,
    color: 'inherit',
  },
  bookThisWeekIcon: {
    width: 18,
    height: 18,
    flexShrink: 0,
    color: 'inherit',
  },
  infoVisualization: {
    width: '100%',
    marginTop: theme.spacing(1),
    textAlign: 'left',
  },
  introVideoDialogPaper: {
    backgroundColor: '#000',
    overflow: 'hidden',
    borderRadius: 12,
    position: 'relative',
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  introVideoDialogClose: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 3,
    cursor: 'pointer',
    color: theme.palette.common.white,
    '& svg': {
      filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.85)) drop-shadow(0 0 4px rgba(0, 0, 0, 0.55))',
    },
    '& path': {
      strokeWidth: 2,
    },
  },
  introVideoDialogContent: {
    padding: 0,
    '&:first-child': {
      paddingTop: 0,
    },
  },
  introVideoDialogLoading: {
    position: 'relative',
    paddingBottom: '56.25%',
    height: 0,
    overflow: 'hidden',
    backgroundColor: '#000',
    '& > *': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
  },
  introVideoDialogError: {
    color: '#fff',
    textAlign: 'center',
    width: '100%',
    maxWidth: 320,
    margin: 0,
  },
}))

export default useStyles
